import { useOptionalPlaybackStoreApiContext } from '@app/core/playback/context/useOptionalPlaybackStoreApiContext';
import { Box, Link } from '@mui/material';
import { useCallback } from 'react';
import { matchChapterLine } from './video/chapters/parse-description-chapters';

type FragmentDescriptionProps = {
  description: string;
};

/**
 * Fragment description with YouTube-style chapter timestamps rendered as links that seek
 * playback to that moment (same `[h:]mm:ss Title` lines the player uses for its chapters menu).
 */
export function FragmentDescription({ description }: FragmentDescriptionProps) {
  const store = useOptionalPlaybackStoreApiContext();

  const seekToSeconds = useCallback(
    (seconds: number) => {
      const state = store?.getState();
      const range = state?.playbackRange;
      if (!state || !range) {
        return;
      }
      state.play({ startTs: range.from + seconds * 1000, playAfterSeek: true });
    },
    [store],
  );

  return (
    <Box sx={{ mt: 1.5, typography: 'body1', color: 'text.primary', whiteSpace: 'pre-wrap' }}>
      {description.split(/\r?\n/).map((line, index) => {
        const chapter = matchChapterLine(line);
        const key = `${index}-${line}`;

        if (!chapter) {
          return <Box key={key} component="span">{`${line}\n`}</Box>;
        }

        const labelStart = line.indexOf(chapter.timeLabel);
        const before = line.slice(0, labelStart);
        const after = line.slice(labelStart + chapter.timeLabel.length);

        return (
          <Box key={key} component="span">
            {before}
            <Link
              component="button"
              type="button"
              onClick={() => seekToSeconds(chapter.start)}
              sx={{ typography: 'body1', verticalAlign: 'baseline' }}
            >
              {chapter.timeLabel}
            </Link>
            {`${after}\n`}
          </Box>
        );
      })}
    </Box>
  );
}
